import React from "react";
import Spinner from 'react-bootstrap/Spinner';
import Modal from 'react-bootstrap/Modal';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import TargetAccountSelector from "../accounting/target-account-selector";
import SyncContactsTable from "./sync-contacts-table";
import SyncContactsConflicts from "./sync-contacts-conflicts";
import { useSyncContacts } from "./hooks/useSyncContacts";

/**
 * Modal component for bidirectional contacts synchronization (Push / Pull)
 * between Contractable and external accounting platforms (Xero, QuickBooks, MYOB).
 *
 * @param {Object} props
 * @param {boolean} props.show - Whether the modal is visible
 * @param {Function} props.onHide - Modal close callback handler
 * @param {'supplier'|'contractor'|'customer'|'all'} props.entityType - Target contact entity type
 * @param {string} [props.connectionId] - Optional platform connection ID to preselect
 * @param {Array<Object>} [props.localContacts] - Optional pre-loaded local contacts array
 * @param {Function} [props.onSyncSuccess] - Callback triggered when sync completes successfully
 * @returns {React.ReactElement}
 */
export default function SyncContactsModal({
    show,
    onHide,
    entityType, // 'supplier' | 'contractor' | 'customer' | 'all'
    connectionId, // Optional: defaults to connection's default
    localContacts: propLocalContacts, // Optional: if passed, avoids fetching
    onSyncSuccess
}) {
    const {
        connections, selectedConnection, setSelectedConnection,
        syncDirection, setSyncDirection,
        isPushAllowed, isPullAllowed,
        syncing, selectedIds, setSelectedIds,
        showConflictModal, setShowConflictModal,
        fetchingPreview, resolutions, setResolutions,
        showAllSyncContacts, setShowAllSyncContacts,
        syncLogs, persistentSyncStatus, syncProgress,
        loadingMetadata, activeModalTab, setActiveModalTab,
        modalCustomers, modalSuppliers, modalContractors,
        getActiveContactsForTab, isAllSelectedForCurrentTab,
        toggleSelectAllForCurrentTab, toggleRowSelection,
        executeSyncContacts, executePullSyncWithResolutions,
        handleClose, getModalTitle, conflicts
    } = useSyncContacts({ show, entityType, connectionId, localContacts: propLocalContacts, onSyncSuccess, onHide });

    return (
        <>
            <Modal show={show} onHide={() => { if (!syncing) handleClose(); }} size="xl" centered dialogClassName="sync-contacts-modal" data-cy="sync-contacts-modal-container">
                <Modal.Header closeButton={!syncing}>
                    <Modal.Title className="text-uppercase font-weight-bold brand-color-3">{getModalTitle()}</Modal.Title>
                </Modal.Header>
                <Modal.Body className="p-4">
                    {entityType === 'all' && loadingMetadata ? (
                        <div className="d-flex justify-content-center align-items-center loader-container">
                            <Spinner animation="border" role="status" variant="primary" />
                            <span className="ms-3">Loading contacts list...</span>
                        </div>
                    ) : (
                        <>
                            <TargetAccountSelector
                                connections={connections}
                                selectedConnection={selectedConnection}
                                onChange={setSelectedConnection}
                                disabled={syncing}
                                dataCyDropdown="sync-contacts-connection-dropdown"
                            />

                            <Form.Group className="mb-4">
                                <Form.Label className="d-block">Sync Direction</Form.Label>
                                <div className="d-flex gap-4">
                                    <Form.Check
                                        type="radio"
                                        id="sync-dir-push-reusable"
                                        name="syncDirectionReusable"
                                        label="Push (Contractable ➔ Accounting Platform)"
                                        checked={syncDirection === 'push'}
                                        onChange={() => setSyncDirection('push')}
                                        data-cy="sync-contacts-dir-push"
                                        disabled={syncing || !isPushAllowed}
                                    />
                                    <Form.Check
                                        type="radio"
                                        id="sync-dir-pull-reusable"
                                        name="syncDirectionReusable"
                                        label="Pull (Accounting Platform ➔ Contractable)"
                                        checked={syncDirection === 'pull'}
                                        onChange={() => setSyncDirection('pull')}
                                        data-cy="sync-contacts-dir-pull"
                                        disabled={syncing || !isPullAllowed}
                                    />
                                </div>
                            </Form.Group>

                            <SyncContactsTable
                                entityType={entityType}
                                activeModalTab={activeModalTab}
                                setActiveModalTab={(k) => {
                                    setActiveModalTab(k);
                                    setSelectedIds([]);
                                }}
                                selectedIds={selectedIds}
                                modalCustomers={modalCustomers}
                                modalSuppliers={modalSuppliers}
                                modalContractors={modalContractors}
                                isAllSelectedForCurrentTab={isAllSelectedForCurrentTab}
                                toggleSelectAllForCurrentTab={toggleSelectAllForCurrentTab}
                                toggleRowSelection={toggleRowSelection}
                                getActiveContactsForTab={getActiveContactsForTab}
                                syncLogs={syncLogs}
                                persistentSyncStatus={persistentSyncStatus}
                                syncing={syncing}
                            />
                            {syncing && syncDirection === 'push' && (
                                <div className="mt-3 p-3 bg-light rounded border border-primary-subtle text-center">
                                    <Spinner size="sm" animation="border" className="me-2 text-primary" />
                                    <span className="fw-bold">
                                        Pushing contacts: {syncProgress.current} of {syncProgress.total}...
                                    </span>
                                </div>
                            )}
                        </>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <span className="me-auto text-muted">
                        {selectedIds.length === 0 ? (
                            syncDirection === 'pull' ? (
                                <span>Selected: <strong>All (Full Pull)</strong></span>
                            ) : (
                                <span>Selected: <strong>All (Full Push)</strong></span>
                            )
                        ) : (
                            <span>Selected: <strong>{selectedIds.length}</strong> contact(s)</span>
                        )}
                    </span>
                    <Button variant="secondary" onClick={handleClose} disabled={syncing} data-cy="sync-contacts-cancel-btn">
                        Cancel
                    </Button>
                    <Button
                        variant="primary"
                        onClick={executeSyncContacts}
                        disabled={syncing || fetchingPreview}
                        data-cy="sync-contacts-submit-btn"
                    >
                        {syncing ? (
                            <>
                                <Spinner size="sm" animation="border" className="me-2" />
                                Syncing...
                            </>
                        ) : fetchingPreview ? (
                            <>
                                <Spinner size="sm" animation="border" className="me-2" />
                                Fetching conflicts...
                            </>
                        ) : (
                            syncDirection === 'pull' ? 'Fetch Conflicts & Preview' : 'Sync Selected'
                        )}
                    </Button>
                </Modal.Footer>
            </Modal>

            <SyncContactsConflicts
                showConflictModal={showConflictModal}
                setShowConflictModal={setShowConflictModal}
                selectedConnection={selectedConnection}
                entityType={entityType}
                conflicts={conflicts}
                resolutions={resolutions}
                setResolutions={setResolutions}
                showAllSyncContacts={showAllSyncContacts}
                setShowAllSyncContacts={setShowAllSyncContacts}
                executePullSyncWithResolutions={executePullSyncWithResolutions}
                syncing={syncing}
            />
        </>
    );
}
